import { JOB_MUTATIONS } from '@/state/mutation-types'
import { JOB_ACTIONS } from '@/state/action-types'
import { jobs } from '@/api/vue-api'
import { RELEVANCE, RELEVANCE_LIST } from '@/utils/enum'

// This method creates a string that we use as a key for our job cache
function createHash(filter, relevance = RELEVANCE.ALL) {
  return `_${filter.search.term}${RELEVANCE_LIST[relevance]._id}${filter.markets.selected}`
}

// creates the string for our markets, depending on the state of the filter and account
function createMarketString(filter) {
  let marketString = ''
  if (filter.markets.selected.length !== 0 && currentAccount !== '') {
    marketString = filter.markets.selected.join(',')
  } else if (currentAccount !== '') {
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const marketKey in filter.markets.list) {
      marketString += `${marketKey},`
    }
  } else {
    marketString = currentMarket
  }
  return marketString
}

// TODO: Going forward I think that all of the filtering should be moved back to the backend at some point
// This function filters a list of job given some filter
function jobsFilter(filter, Jobs) {
  let filtered = Jobs
  // Categories Filter
  if (filter.categories.selected && filter.categories.selected.length > 0) {
    filtered = filtered.filter(
      (job) =>
        filter.categories.selected.length >= job.category_id &&
        filter.categories.selected[job.category_id] != null,
    )
  }

  // Regions Filter
  if (filter.regions.selected && filter.regions.selected.length > 0) {
    const tempRegionFilter = []
    const tempMarketFilter = []

    if (filter.regions.list !== null) {
      filter.regions.selected.forEach((region) => {
        filter.markets.selected.forEach((market) => {
          if (filter.regions.list[market] && filter.regions.list[market][region] != null) {
            tempRegionFilter[region] = region
            tempMarketFilter[market] = market
          }
        })
      })
    }

    filtered = filtered.filter(
      (job) => tempRegionFilter[job.region_id] != null || tempMarketFilter[job.market] == null,
    )
  }

  // Type Filter
  if (filter.type.selected !== '' && filter.type.selected != null) {
    filtered = filtered.filter((job) => {
      if (filter.type.selected === 'hasRate') {
        return ['hourly', 'daily'].includes(job.ratetype)
      }
      return job.ratetype === filter.type.selected
    })
  }

  // Asc or decs
  if (filter.sortBy === 'asc') {
    // .slice() needs to be added to prevent an infinite loop in the render function with vue
    return filtered
      .slice()
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
      .slice()
  }
  return filtered
    .slice()
    .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
    .slice()
}

export default {
  strict: import.meta.env.VITE_APP_ENV !== 'production',
  state: {
    cache: {},
    displayKey: '', // this is the current job cache we want to display
    recommended: [],
  },
  mutations: {
    [JOB_MUTATIONS.UPDATE_LIST](state, list) {
      state.cache[state.displayKey] = list
    },
    [JOB_MUTATIONS.ADD_TO_CACHE](state, value) {
      state.cache[value.key] = value.data
    },
    [JOB_MUTATIONS.SET_KEY](state, key) {
      state.displayKey = key
    },
    [JOB_MUTATIONS.SET_RECOMMENDED_JOBS](state, list) {
      state.recommended = list
    },
    [JOB_MUTATIONS.SET_MATCHED_JOBS](state, list) {
      state.matchedJobs = list
    },
    [JOB_MUTATIONS.SET_ALL_JOBS](state, list) {
      state.allJobs = list
    },
  },
  actions: {
    /**
     * This method prepares the filter and cache for job searching and dispatches the searches.
     * Then when it is done searching it returns the list of jobs from the current filter selection.
     * @param filter
     * @returns {Promise<Array>}
     */
    [JOB_ACTIONS.SEARCH_JOBS]({ commit, state, dispatch, getters }, filter) {
      return new Promise(async (resolve, reject) => {
        let response = {}

        if (!filter.search.term || filter.search.term == null) {
          filter.search.term = ''
        }

        // Here we create the key we will use for our cache
        const cacheKey = createHash(filter, filter.relevance.selected)
        commit(JOB_MUTATIONS.SET_KEY, cacheKey)

        if (!state.cache[cacheKey]) {
          // We set the status to searching so we won't be doing it twice at the same time
          commit(JOB_MUTATIONS.ADD_TO_CACHE, {
            data: 'searching',
            key: cacheKey,
          })

          // Start getting the data
          try {
            // Here we update our search for all types
            dispatch(JOB_ACTIONS.DISPATCH_SEARCH_JOBS, filter)

            const marketString = createMarketString(filter)
            response = await jobs.getJobsByQuery(
              filter.search.term,
              filter.relevance.selectedID,
              marketString,
            )
            // We cache the response
            commit(JOB_MUTATIONS.ADD_TO_CACHE, {
              data: response.data,
              key: cacheKey,
            })

            resolve()
          } catch (e) {
            reject(e)
          }
        } else if (state.cache[cacheKey] === 'searching') {
          resolve('searching')
        } else {
          response.data = state.cache[cacheKey]
        }

        resolve(getters.jobList)
      })
    },
    /**
     * Here we take a filter store, and get jobs for both the relevant jobs, and all the jobs.
     * We then store them in their own lists
     * @param filter
     * @returns {Promise<void>}
     */
    async [JOB_ACTIONS.DISPATCH_SEARCH_JOBS]({ commit, dispatch, state }, filter) {
      const marketString = createMarketString(filter)
      // We only want to start a search, if we don't already have it saved in the cache.
      if (state.cache[createHash(filter, RELEVANCE.ALL)] === undefined) {
        const allJobsResponse = await jobs.getJobsByQuery(
          filter.search.term,
          RELEVANCE_LIST[RELEVANCE.ALL]._id,
          marketString,
        )
        // We cache the response
        commit(JOB_MUTATIONS.ADD_TO_CACHE, {
          data: allJobsResponse.data,
          key: createHash(filter, RELEVANCE.ALL),
        })
      }

      // We make sure that we have an account to actually get matched jobs on
      if (
        typeof currentAccount === 'object' &&
        state.cache[createHash(filter, RELEVANCE.MATCH)] === undefined
      ) {
        const matchedResponse = await jobs.getJobsByQuery(
          filter.search.term,
          RELEVANCE_LIST[RELEVANCE.MATCH]._id,
          marketString,
        )
        // We cache the response
        commit(JOB_MUTATIONS.ADD_TO_CACHE, {
          data: matchedResponse.data,
          key: createHash(filter, RELEVANCE.MATCH),
        })
      }
    },
    /**
     * This function initiates the job vuex store
     * @param commit
     * @returns {Promise<any>}
     */
    [JOB_ACTIONS.INITIATE_JOBS]({ commit, rootState }) {
      return new Promise(async (resolve, reject) => {
        try {
          const markets =
            currentAccount && currentAccount.markets
              ? currentAccount.markets.map((m) => m.market)
              : [currentMarket]

          const h = createHash(
            { search: { term: '' }, markets: { selected: markets.sort() } },
            RELEVANCE.ALL,
          )
          commit(JOB_MUTATIONS.ADD_TO_CACHE, {
            data: 'searching',
            key: h,
          })

          const response = await jobs.getRecommendedJobs()
          const matchedResponse = await jobs.getJobsByQuery(
            '',
            RELEVANCE_LIST[RELEVANCE.ALL]._id,
            markets.sort().join(),
          )

          if (response && response.data) {
            commit(JOB_MUTATIONS.SET_RECOMMENDED_JOBS, response.data)
          } else {
            commit(JOB_MUTATIONS.SET_RECOMMENDED_JOBS, [])
          }
          commit(JOB_MUTATIONS.ADD_TO_CACHE, {
            data: matchedResponse.data,
            key: h,
          })

          resolve()
        } catch (e) {
          reject(e)
        }
      })
    },
  },
  getters: {
    // Takes the current job list and runs it through the frontend filter
    jobsListFiltered: (state, getters, rootState, rootGetters) =>
      jobsFilter(rootGetters.filter, getters.jobList),

    // Returns the jobs that the current state of the filter denotes
    jobList: (state) => {
      const list = state.cache[state.displayKey]
      if (list && typeof list !== 'string') {
        return list
      }
      return []
    },

    // Simply checks if the current filter state is searching for its jobs
    searchingForCurrentJobs: (state) => state.cache[state.displayKey] === 'searching',

    // We have a list of jobs that we like to show called the recommended list, it is jobs from different markets
    // that pop up at the end if your matched jobs, to entice the freelancer to sign up into more markets.
    // This method returns jobs the recommended list where the markets are not selected yet
    recommendList: (state, getters, rootState, rootGetters) => {
      if (state.recommended) {
        // Convert markets selected into dictionary table with markets as keys
        if (rootGetters.filter.markets.selected) {
          const markets = rootGetters.filter.markets.selected.reduce((reverse, market) => {
            reverse[market] = market
            return reverse
          }, {})
          // Filter out jobs that are in the current markets, as we only want the ones from other markets
          return state.recommended.filter((job) => markets[job.market] == null)
        }
        return state.recommended
      }
      return []
    },

    // Returns all the matched jobs a user might have no matter the state of the filter
    allMatchedJobs: (state) => {
      // Here we check to see if we have an account, otherwise we will have to return an empty list
      if (typeof currentAccount !== 'object') return []

      // Here we create an empty filter so that we always will get all the jobs
      const h = createHash(
        {
          search: { term: '' },
          markets: { selected: currentAccount.markets.map(({ market }) => market).sort() },
        },
        RELEVANCE.MATCH,
      )

      return state.cache[h] &&
        typeof state.cache[h] !== 'string' &&
        state.cache[h].length > 0 &&
        state.cache[h][0]['no-jobs-msg'] === undefined
        ? state.cache[h]
        : []
    },
    allClientsJobs: (state) => {
      let markets = []

      if (typeof currentAccount !== 'object') {
        markets = [currentMarket]
      } else {
        markets = currentAccount.markets
      }

      // Here we create an empty filter so that we always will get all the jobs
      const h = createHash(
        { search: { term: '' }, markets: { selected: markets.map(({ market }) => market).sort() } },
        RELEVANCE.CLIENTS,
      )

      return state.cache[h] &&
        typeof state.cache[h] !== 'string' &&
        state.cache[h].length > 0 &&
        state.cache[h][0]['no-jobs-msg'] === undefined
        ? state.cache[h]
        : []
    },
  },
}
